import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Loader2, Search, MessageSquare, X, Mail, FileText, FileJson, Filter } from 'lucide-react';
import { listAdminSessions, getAdminSession } from '../services/adminApi';

function csvEscape(value) {
    if (value == null) return '';
    const s = String(value).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function downloadCsv(rows, columns, filename) {
    const head = columns.join(',');
    const body = rows.map(r => columns.map(c => csvEscape(r[c])).join(',')).join('\n');
    const csv = `${head}\n${body}`;
    downloadBlob(new Blob(["﻿" + csv], { type: 'text/csv;charset=utf-8;' }), filename);
}

function downloadJson(obj, filename) {
    const json = JSON.stringify(obj, null, 2);
    downloadBlob(new Blob([json], { type: 'application/json' }), filename);
}

function flattenSession(s) {
    const summary = s.summary || {};
    const survey = s.survey || null;
    // El email del usuario puede venir de dos fuentes:
    //  1. Del state de la sesión (capturado en el modal de inicio).
    //  2. De la encuesta final que envió.
    // Preferimos el state (presente desde el primer turno) y caemos al survey.
    const email = summary.user_email || survey?.email || '';
    return {
        thread_id: s.thread_id,
        last_ts: s.last_ts || '',
        scenario: summary.scenario_nombre || '',
        personaje: summary.scenario_personaje || '',
        tipo_proyecto: summary.scenario_tipo_proyecto || '',
        habilidad: summary.habilidad_actual || '',
        nivel: summary.nivel ?? '',
        phase: summary.phase || '',
        message_count: summary.message_count ?? 0,
        user_messages: summary.user_message_count ?? 0,
        feedback_ready: summary.feedback_ready ? 'sí' : 'no',
        progress: summary.progress != null ? Math.round(Math.max(0, Math.min(1, summary.progress)) * 100) + '%' : '',
        duration_seconds: summary.session_duration_seconds ?? null,
        email,
        survey_at: survey?.submitted_at || '',
        client_id: summary.client_id || survey?.client_id || '',
    };
}

function formatDuration(seconds) {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const total = Math.max(0, Math.round(seconds));
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s}s`;
    return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatTs(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now - d;
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    if (diffMs < minute) return 'ahora';
    if (diffMs < hour) return `${Math.floor(diffMs / minute)}m`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)}h`;
    if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d`;
    return d.toLocaleDateString();
}

export default function AdminSessionsTable() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ from: '', to: '' });
    const [openSession, setOpenSession] = useState(null); // detalle full

    const fetchData = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await listAdminSessions({
                limit: 200,
                from: filters.from || undefined,
                to: filters.to || undefined,
            });
            setItems(res.items || []);
        } catch (err) {
            setError(err.message || 'Error cargando sesiones');
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

    const flat = useMemo(() => items.map(flattenSession), [items]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return flat;
        return flat.filter(r =>
            (r.scenario || '').toLowerCase().includes(q)
            || (r.email || '').toLowerCase().includes(q)
            || (r.thread_id || '').toLowerCase().includes(q)
            || (r.habilidad || '').toLowerCase().includes(q)
            || (r.client_id || '').toLowerCase().includes(q)
        );
    }, [flat, search]);

    const stats = useMemo(() => {
        const withFb = filtered.filter(r => r.feedback_ready === 'sí').length;
        const withSurvey = filtered.filter(r => r.email || r.survey_at).length;
        const messages = filtered.reduce((a, r) => a + (r.message_count || 0), 0);
        const uniqueClients = new Set(filtered.map(r => r.client_id).filter(Boolean)).size;
        return { count: filtered.length, withFb, withSurvey, messages, uniqueClients };
    }, [filtered]);

    const exportSessionsCsv = () => {
        const cols = [
            'thread_id', 'scenario', 'tipo_proyecto', 'personaje', 'habilidad', 'nivel',
            'phase', 'message_count', 'user_messages', 'feedback_ready', 'progress',
            'duration_seconds',
            'email', 'survey_at', 'client_id',
        ];
        downloadCsv(filtered, cols, `sessions-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    const exportAllMessagesCsv = async () => {
        setLoading(true);
        try {
            const detailRows = [];
            for (const r of filtered) {
                try {
                    const detail = await getAdminSession(r.thread_id);
                    (detail.messages || []).forEach((m, idx) => {
                        detailRows.push({
                            thread_id: r.thread_id,
                            scenario: r.scenario,
                            habilidad: r.habilidad,
                            email: r.email,
                            turn: idx + 1,
                            role: m.role,
                            content: m.content,
                        });
                    });
                } catch (_) { /* skip session that fails */ }
            }
            const cols = ['thread_id', 'scenario', 'habilidad', 'email', 'turn', 'role', 'content'];
            downloadCsv(detailRows, cols, `messages-${new Date().toISOString().slice(0, 10)}.csv`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="Sesiones" value={stats.count} />
                <Stat label="Con feedback" value={stats.withFb} />
                <Stat label="Con encuesta" value={stats.withSurvey} />
                <Stat label="Mensajes totales" value={stats.messages} />
                <Stat label="Usuarios únicos" value={stats.uniqueClients} />
            </div>

            {/* Toolbar */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Buscar (escenario, email, thread_id, habilidad)…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-500"
                    />
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-300">
                    <Filter className="w-3.5 h-3.5 text-slate-400" />
                    <span>Desde</span>
                    <input
                        type="date"
                        value={filters.from}
                        onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
                        className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-white"
                    />
                    <span>hasta</span>
                    <input
                        type="date"
                        value={filters.to}
                        onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
                        className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-xs text-white"
                    />
                </div>
                <button
                    onClick={fetchData}
                    disabled={loading}
                    className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-slate-700/60 hover:bg-slate-600 text-white disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Aplicar
                </button>
                <button
                    onClick={exportSessionsCsv}
                    disabled={!filtered.length || loading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold disabled:opacity-40"
                    title="Exporta el resumen de cada sesión (1 fila = 1 sesión)"
                >
                    <FileText className="w-3.5 h-3.5" />
                    CSV resumen
                </button>
                <button
                    onClick={exportAllMessagesCsv}
                    disabled={!filtered.length || loading}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold disabled:opacity-40"
                    title="Exporta todas las conversaciones (1 fila = 1 mensaje). Más lento."
                >
                    <FileText className="w-3.5 h-3.5" />
                    CSV mensajes
                </button>
            </div>

            {error && (
                <div className="text-sm text-red-300 bg-red-900/30 border border-red-700/40 rounded-lg px-3 py-2">
                    {error}
                </div>
            )}

            {/* Tabla — fila clickeable + scroll interno (vertical y horizontal)
                con thead sticky-top y columna acciones sticky-right. Asi la barra
                horizontal queda dentro del card, no al final de la pagina. */}
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl overflow-hidden">
                <div className="overflow-auto max-h-[55vh] min-h-[260px]">
                    <table className="min-w-full text-xs">
                        <thead className="text-slate-300 uppercase tracking-wider sticky top-0 z-20">
                            <tr>
                                <Th sticky>Última actividad</Th>
                                <Th sticky>Escenario</Th>
                                <Th sticky>Habilidad · Nivel</Th>
                                <Th sticky>Fase</Th>
                                <Th sticky>Turnos</Th>
                                <Th sticky>Duración</Th>
                                <Th sticky>Progreso</Th>
                                <Th sticky>Encuesta</Th>
                                <Th sticky>Thread</Th>
                                <th className="sticky right-0 top-0 z-30 bg-slate-800/95 backdrop-blur text-left font-semibold px-3 py-2 whitespace-nowrap shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)]">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {loading && !filtered.length ? (
                                <tr><td colSpan={10} className="text-center py-6 text-slate-400">
                                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Cargando…
                                </td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan={10} className="text-center py-6 text-slate-500">Sin resultados</td></tr>
                            ) : filtered.map((r) => (
                                <tr
                                    key={r.thread_id}
                                    className="hover:bg-slate-800/40 cursor-pointer transition-colors"
                                    onClick={() => setOpenSession({ thread_id: r.thread_id })}
                                >
                                    <Td className="text-slate-300 whitespace-nowrap" title={r.last_ts}>
                                        {formatTs(r.last_ts)}
                                    </Td>
                                    <Td className="text-slate-200 max-w-[220px] truncate" title={r.scenario}>
                                        {r.scenario || '—'}
                                    </Td>
                                    <Td className="text-slate-300 max-w-[200px] truncate" title={r.habilidad}>
                                        {r.habilidad || '—'}
                                        {r.nivel ? <span className="text-slate-500"> · N{r.nivel}</span> : null}
                                    </Td>
                                    <Td>
                                        <PhaseBadge phase={r.phase} feedbackReady={r.feedback_ready === 'sí'} />
                                    </Td>
                                    <Td className="text-slate-300 whitespace-nowrap">
                                        {r.message_count} <span className="text-slate-500">({r.user_messages}u)</span>
                                    </Td>
                                    <Td className="text-slate-300 whitespace-nowrap" title={r.duration_seconds != null ? `${r.duration_seconds}s` : ''}>
                                        {formatDuration(r.duration_seconds)}
                                    </Td>
                                    <Td className="text-slate-300">{r.progress || '—'}</Td>
                                    <Td>
                                        {r.email ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-300" title={`Enviada ${r.survey_at || ''}`}>
                                                <Mail className="w-3 h-3" />
                                                <span className="max-w-[140px] truncate">{r.email}</span>
                                            </span>
                                        ) : <span className="text-slate-500">—</span>}
                                    </Td>
                                    <Td className="text-slate-400 font-mono text-[10px]" title={r.thread_id}>
                                        {(r.thread_id || '').slice(0, 12)}…
                                    </Td>
                                    <td
                                        className="sticky right-0 z-10 bg-slate-900/95 backdrop-blur px-3 py-2 align-top shadow-[-8px_0_8px_-6px_rgba(0,0,0,0.4)] group-hover:bg-slate-800/95"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            onClick={() => setOpenSession({ thread_id: r.thread_id })}
                                            className="text-xs px-2.5 py-1 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold inline-flex items-center gap-1 whitespace-nowrap"
                                        >
                                            <MessageSquare className="w-3 h-3" />
                                            Ver chat
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {openSession && (
                <SessionDetailModal
                    threadId={openSession.thread_id}
                    onClose={() => setOpenSession(null)}
                />
            )}
        </div>
    );
}

function SessionDetailModal({ threadId, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        getAdminSession(threadId)
            .then(d => { if (!cancelled) setData(d); })
            .catch(e => { if (!cancelled) setError(e.message || 'Error cargando sesión'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [threadId]);

    const exportJson = () => {
        if (!data) return;
        downloadJson(data, `session-${threadId.slice(0, 12)}.json`);
    };

    const exportCsv = () => {
        if (!data) return;
        const cols = ['turn', 'role', 'content'];
        const rows = (data.messages || []).map((m, i) => ({ turn: i + 1, role: m.role, content: m.content }));
        downloadCsv(rows, cols, `session-${threadId.slice(0, 12)}.csv`);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
                <div className="px-5 py-3 border-b border-slate-700/60 flex items-center justify-between">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-white">Conversación</h3>
                        <p className="text-[10px] text-slate-500 font-mono truncate">{threadId}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={exportCsv} disabled={!data} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-slate-700/60 hover:bg-slate-600 text-white disabled:opacity-40">
                            <FileText className="w-3.5 h-3.5" /> CSV
                        </button>
                        <button onClick={exportJson} disabled={!data} className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold disabled:opacity-40">
                            <FileJson className="w-3.5 h-3.5" /> JSON
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="px-5 py-4 overflow-y-auto flex-1 bg-slate-950/40">
                    {loading ? (
                        <div className="text-center text-slate-400 py-8">
                            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…
                        </div>
                    ) : error ? (
                        <div className="text-sm text-red-300">{error}</div>
                    ) : data ? (
                        <>
                            {data.summary?.scenario_nombre && (
                                <div className="mb-4 text-xs text-slate-400 border border-slate-700/40 rounded-lg p-3 bg-slate-800/30">
                                    <p><span className="text-slate-500">Escenario:</span> <span className="text-slate-200">{data.summary.scenario_nombre}</span></p>
                                    {data.summary.scenario_personaje && <p><span className="text-slate-500">Personaje:</span> {data.summary.scenario_personaje}</p>}
                                    {data.summary.habilidad_actual && <p><span className="text-slate-500">Habilidad:</span> {data.summary.habilidad_actual} · Nivel {data.summary.nivel ?? '—'}</p>}
                                </div>
                            )}
                            <div className="space-y-2">
                                {(data.messages || []).length === 0 ? (
                                    <p className="text-slate-500 text-sm">Sin mensajes</p>
                                ) : data.messages.map((m, i) => (
                                    <div key={i} className={`flex ${m.role === 'human' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[85%] rounded-2xl px-4 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                            m.role === 'human'
                                                ? 'bg-emerald-500 text-slate-900'
                                                : 'bg-slate-800 text-slate-100 border border-slate-700/60'
                                        }`}>
                                            {m.content}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {data.feedback_message && (
                                <div className="mt-5 border-t border-slate-700/40 pt-4">
                                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Feedback final</p>
                                    <div className="text-xs text-slate-300 whitespace-pre-wrap bg-slate-800/40 border border-slate-700/40 rounded-lg p-3 max-h-72 overflow-y-auto">
                                        {data.feedback_message}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-xl font-bold text-white mt-0.5">{value}</p>
        </div>
    );
}

function Th({ children, sticky = false }) {
    // Cuando el thead es sticky-top, cada celda necesita su propio fondo opaco
    // para que las filas que pasen por detras no se transparenten.
    const cls = sticky
        ? 'text-left font-semibold px-3 py-2 whitespace-nowrap bg-slate-800/95 backdrop-blur'
        : 'text-left font-semibold px-3 py-2 whitespace-nowrap';
    return <th className={cls}>{children}</th>;
}

function Td({ children, className = '' }) {
    return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}

function PhaseBadge({ phase, feedbackReady }) {
    if (feedbackReady) {
        return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">feedback</span>;
    }
    if (!phase) return <span className="text-slate-500">—</span>;
    return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-700/60 text-slate-300 border border-slate-600/40">{phase}</span>;
}
